<template>
    <div class="card">
        <DataTable
            v-model:first="offset"
            :rows="numberOfRows"
            :paginator="true"
            :lazy="true"
            selectionMode="single"
            @page="onPage"
            :totalRecords="totalBlocks"
            :rowsPerPageOptions="[5, 10, 20, 50]"
            paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"


            v-model:selection="selectedBlocks"
            :value="blocks"
            :resizableColumns="true"
            ref="dt"
            dataKey="id"
            v-model:filters="filters"
            :rowHover="true"
            :loading="loading"
            responsiveLayout="scroll"
        >
            <template #header>
                <div class="flex justify-content-between flex-column sm:flex-row">
                    <div class="flex">
                        <Button v-if="isGodUser" :label="shared === 'Company' ? 'Export to Shared' : 'Import to Company'" class="p-button-warning mr-2" @click="confirmTransfer" />
                    </div>
                    <div class="flex">
                        <div>

                            <span>
                                <label for="filter">Filter By:</label>
                                <Dropdown class="mx-2" v-model="shared" :options="['Shared', 'Company']" placeholder="Select Option" />
                            </span>
                            <span>
                                <label for="filter">Status:</label>
                                <Dropdown class="mx-2" v-model="params.default" optionLabel="name" optionValue="value" :options="default_types" placeholder="Select Option" showClear />
                            </span>
                        </div>
                        <div class="p-input-icon-left mb-2">
                            <i :class="['pi', { 'pi-search': !filters['global'].loading }]" />
                            <InputText maxlength = "255" v-model="params.code" placeholder="Keyword Search" style="width: 100%" />
                        </div>
                        <Button type="button" icon="pi pi-filter" label="Filter" class="p-button-outlined p-button-success mb-2 ml-2" @click="applyFilter" />
                        <Button type="button" icon="pi pi-filter-slash" label="Clear" class="p-button-outlined mb-2 p-button-danger ml-2" @click="clearFilter" />
                    </div>
                </div>
            </template>
            <template #empty> No blocks available. </template>
            <template #loading> Loading Blocks. Please wait. </template>
            <Column selectionMode="multiple" headerStyle="width: 3rem" />
            <Column field="id" header="ID" style="width: 4rem" />
            <Column field="name" header="NAME" style="max-width: 10rem" />
            <Column field="code" header="CODE" style="max-width: 8rem" />
            <Column field="description" header="DESCRIPTION" style="max-width: 14rem" />
            <Column field="default" header="DEFAULT" style="width: 5rem">
                <template #body="slotProps">
                    <Tag :severity="slotProps.data.default == 1 ? '' : 'danger'" :value="slotProps.data.default == 1 ? 'True' : 'False'" rounded />
                </template>
            </Column>
            <Column field="attributes" header="ATTRIBUTES" style="max-width: 6rem">
                <template #body="slotProps">
                    {{ slotProps.data.attributes.length }}
                </template>
            </Column>
            <Column field="created_at" header="CREATED AT" style="width: 6em">
                <template #body="slotProps">
                    {{ formattedDate(slotProps.data.created_at) }}
                </template>
            </Column>
            <Column field="actions" header="ACTIONS">
                <template #body="slotProps">
                    <Button icon="pi pi-eye" class="p-button-rounded p-button-info mr-2" @click="operateBlock(slotProps.data.id, 'view')" v-tooltip="'View Block Details'" />
                    <Button icon="pi pi-briefcase" class="p-button-rounded p-button-warning mr-2" @click="operateBlock(slotProps.data.id, 'rule')" v-tooltip="'Add Block Rules'" />
                    <Button v-if="checkUserPermission(permissions.WRITE_ASSESSMENT_BLOCKS)" icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2" @click="operateBlock(slotProps.data.id, 'edit')" v-tooltip="'Edit Block'" />
                    <Button v-if="checkUserPermission(permissions.DELETE_ASSESSMENT_BLOCKS)" icon="pi pi-trash" class="p-button-rounded p-button-danger mr-2" @click="confirmDelete(slotProps.data.id)" v-tooltip="'Delete Block'" />
                    <Button
                        v-if="checkUserPermission(permissions.WRITE_ASSESSMENT_BLOCKS) && slotProps.data.default !== 1"
                        icon="pi pi-check"
                        class="p-button-rounded p-button-help"
                        @click="operateBlock(slotProps.data.id, 'default')"
                        v-tooltip="'Set Default'"
                    />
                </template>
            </Column>
        </DataTable>
    </div>
    <block-details v-if="blockDetailsDialog" :block="blockDetails" @hide-dialog="blockDetailsDialog = false" />
    <!-- Loader Dialog -->
    <DefaultLoader v-if="operationProgress" />
</template>

<script>
import BlockDetails from './BlockDetails.vue';
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useConfirm } from 'primevue/useconfirm';
import { FilterMatchMode } from 'primevue/api';

import useUserComposable from '@/composables/UserComposable';
import { useBlockStore } from '../../products/pha/modules/block/store';
import { formatDate } from '@/helpers/date-formatter';
import { showToast } from '@/helpers/toast-handler';
import { SharedTypes, UserPermissions } from '../../enums';
import {
    buildPagination, getFilters,
    getPage,
    getPerPage,
    setFilters,
    setPage,
    setPerPage,
} from '@/composables/PaginationComposable';

export default {
    name: 'BlockTable',
    components: { BlockDetails },
    emits: ['reload-table'],
    props: {
        isGodUser: {
            type: Boolean,
        },
        userCompanyId: {
            type: Number,
        },
        reloadTable: {
            type: Boolean,
        },
    },
    setup() {
        const blockStore = useBlockStore();
        const { loading } = storeToRefs(useBlockStore());
        const selectedBlocks = ref([]);
        const operationProgress = ref(false);
        const permissions = UserPermissions;
        const { checkUserPermission } = useUserComposable();

        const allBlocks = computed(() => {
            return blockStore.blocks;
        });
        const totalBlocks = computed(() => {
            return blockStore.totalBlocks;
        });

        const confirm = useConfirm();
        const shared = ref('Company');

        const confirmDelete = (id) => {
            confirm.require({
                message: 'Are you sure you want to delete block' + id + '?',
                header: 'Confirmation',
                icon: 'pi pi-exclamation-triangle',
                accept: async () => {
                    operationProgress.value = true;
                    await blockStore.deleteBlock(shared.value, id);
                    operationProgress.value = false;
                },
                reject: () => {
                    showToast({ summary: 'Rejected' }, 'Block not deleted');
                },
            });
        };

        const transferBlocks = async (exportFlag, companyId) => {
            try {
                operationProgress.value = true;
                // make a loop and export of them
                const total = selectedBlocks.value?.length;
                let transferCount = 0;
                for (let i = 0; i < selectedBlocks.value?.length; i++) {
                    const block = selectedBlocks?.value[i];
                    const transfered = exportFlag ? await blockStore.exportCompanyBlockToShared(block.id) : await blockStore.importSharedBlocksToCompany(block.id, companyId);
                    // if transfered flag is true then add the count
                    if (transfered) {
                        transferCount++;
                    }
                    // debugger;
                    if (transferCount === total) {
                        showToast({ severity: 'success', summary: 'Success' }, `Successfully transfered ${total} blocks`);
                    }
                }
            } catch (error) {
                showToast({}, error?.data?.message ?? 'Error while transfering');
            } finally {
                operationProgress.value = false;
            }
        };

        return {
            // store
            blockStore,
            // params
            shared,
            loading,
            operationProgress,
            // states
            blocks: allBlocks,
            selectedBlocks,
            permissions,
            // actions
            confirmDelete,
            transferBlocks,
            checkUserPermission,
            totalBlocks,
        };
    },
    data() {
        return {
            default_types: [
                { name: 'Active Only', value: true },
                { name: 'Inactive Only', value: false },
            ],
            filters: null,
            globalFilterValue: null,
            params: {
                //categories: [],
                code: null,
                //code_exact: null,
                default: true,
                company_id: this.userCompanyId,
            },
            blockDetails: {},
            blockDetailsDialog: false,
        };
    },
    computed: {
        offset: function () {
            return getPage('blockList') * getPerPage('blockList')
        },
        numberOfRows: function () {
            return getPerPage('blockList')
        },
    },
    watch: {
        reloadTable: {
            handler() {
                this.loadBlocks();
            },
        },
        shared: {
            handler() {
                if (this.shared && this.shared == 'Company') {
                    this.params.company_id = this.userCompanyId;
                } else {
                    delete this.params.company_id;
                }
                //this.loadBlocks();
            },
        },
        'params.default': {
            handler() {
                //this.loadBlocks();
            },
        },
    },
    created() {
        this.initFilters();
    },
    async mounted() {
        buildPagination('blockList', 0, 10, [{field: "id", order: -1}])
        this.loadBlocks();
    },
    methods: {
        initFilters() {
            console.log('initFilters::getFilters', getFilters('blockList'))
            console.log('initFilters::params', this.params)

            this.shared = 'Company';

            const filterToBeApplied = getFilters('blockList')
            if (typeof filterToBeApplied !== 'undefined') {
                this.params = filterToBeApplied
                if (typeof filterToBeApplied.company_id === 'undefined') {
                    this.shared = 'Shared'
                }
            }

            this.filters = {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS, loading: false },
            };
        },
        clearFilter() {
            this.initFilters();
        },
        async applyFilter() {
            setFilters('blockList', this.params)
            await this.loadBlocks()
        },
        async loadBlocks() {
            await this.blockStore.fetchAllBlocks(this.shared, this.params, 'blockList');
        },
        formattedDate(date) {
            return date ? formatDate(date) : '';
        },
        async operateBlock(id, mode) {
            if (mode == 'edit') {
                this.$router.push({ path: `blocks/editBlock/${this.shared}/${id}` });
            } else if (mode == 'view') {
                this.blockDetailsDialog = true;
                this.blockDetails = await this.blockStore.fetchBlockDetails(this.shared, id);
            } else if (mode == 'default') {
                await this.confirmDefault(id);
            } else if (mode == 'rule') {
                this.$router.push({ name: 'BoxBuilder', params: { blockId: id } });
            }
        },
        confirmDefault(id) {
            this.$confirm.require({
                header: 'Confirmation',
                message: 'Are you sure you want to set the block Id ' + id + ' as default version?',
                icon: 'pi pi-exclamation-triangle',
                accept: async () => {
                    await this.blockStore.setBlockDefault(id, this.shared);
                    this.$emit('reload-table', true);
                },
                reject: () => {
                    this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Cancelled', life: 5000 });
                },
            });
        },
        confirmTransfer() {
            this.$confirm.require({
                header: 'Confirmation',
                message: 'Are you sure you want export the selected blocks?',
                icon: 'pi pi-exclamation-triangle',
                accept: async () => {
                    // if SharedType = company, then export to shared, then set flag is true
                    // else import to company
                    this.transferBlocks(this.shared === SharedTypes.COMPANY, this.userCompanyId);
                },
                reject: () => {
                    this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Export Cancelled', life: 5000 });
                },
            });
        },

        async onPage(event) {
            console.log('onPage', event);
            setPage('blockList', event.page)
            setPerPage('blockList', event.rows)
            await this.blockStore.fetchAllBlocks(this.shared, this.params, 'blockList');
        },
    },
};
</script>
